// In dev: vite proxies /api to the deployed function app.
// In prod (SWA linked-backend): /api routes through the SWA to the Function App automatically.
const API_BASE = '';

export interface JobSubmitRequest {
  url: string;
  designMd: string;
  sourceUrl?: string;
  model?: string;
}

export interface JobSubmitResponse {
  jobId: string;
  status: string;
  model: string;
}

export interface ReadyzResponse {
  status: string;
  version: string;
  commit: string;
  shortCommit: string;
}

export interface JobMeta {
  jobId: string;
  status: number; // 0 Queued, 1 Running, 2 Completed, 3 Failed
  agentVersion: string;
  commit: string;
  submittedAt: string;
  startedAt: string | null;
  completedAt: string | null;
  model: string;
  error: string | null;
}

export interface JobGetResponse {
  meta: JobMeta;
  response: unknown | null;
}

export interface ProblemDetails {
  type: string;
  title: string;
  status: number;
  detail?: string;
  instance?: string;
  traceId?: string;
  retryAfterSeconds?: number;
}

async function handleResponse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const ct = res.headers.get('content-type') ?? '';
    if (ct.includes('problem+json') || ct.includes('json')) {
      const problem = (await res.json()) as ProblemDetails;
      throw Object.assign(new Error(problem.detail ?? problem.title), { problem });
    }
    throw new Error(`HTTP ${res.status} ${res.statusText}`);
  }
  return (await res.json()) as T;
}

export async function getReadyz(): Promise<ReadyzResponse> {
  return handleResponse<ReadyzResponse>(await fetch(`${API_BASE}/api/readyz`));
}

export interface ModelInfo {
  name: string;
  tier: string;
  description: string;
  vision: boolean;
  reasoning: boolean;
  isDefault: boolean;
}

export async function getModels(): Promise<{ count: number; models: ModelInfo[] }> {
  return handleResponse(await fetch(`${API_BASE}/api/models`));
}

export async function submitJob(req: JobSubmitRequest): Promise<JobSubmitResponse> {
  const res = await fetch(`${API_BASE}/api/jobs`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(req),
  });
  return handleResponse<JobSubmitResponse>(res);
}

export async function getJob(jobId: string): Promise<JobGetResponse> {
  return handleResponse<JobGetResponse>(await fetch(`${API_BASE}/api/jobs/${jobId}`));
}

export const JobStatus = {
  Queued: 0,
  Running: 1,
  Completed: 2,
  Failed: 3,
} as const;

export function jobStatusLabel(s: number): string {
  return ['Queued', 'Running', 'Completed', 'Failed'][s] ?? 'Unknown';
}
